// Lightweight i18n for 2 locales. EN keys are the type source; AR mirrors them.
// No deps; total runtime ~1KB.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import en from './en.json'
import ar from './ar.json'

export type Locale = 'en' | 'ar'
export type Dict = typeof en
export type TKey = keyof Dict

const DICTS: Record<Locale, Dict> = { en, ar: ar as Dict }
const LANG_KEY = 'egyptle.lang'

function detectInitial(): Locale {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(LANG_KEY) as Locale | null
    if (stored === 'en' || stored === 'ar') return stored
  }
  if (typeof navigator !== 'undefined') {
    return navigator.language?.toLowerCase().startsWith('ar') ? 'ar' : 'en'
  }
  return 'en'
}

type Ctx = {
  locale: Locale
  setLocale: (l: Locale) => void
  t: (k: TKey, vars?: Record<string, string | number>) => string
}

const LocaleCtx = createContext<Ctx>({
  locale: 'en',
  setLocale: () => {},
  t: (k) => k,
})

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, _setLocale] = useState<Locale>(detectInitial)

  useEffect(() => {
    document.documentElement.lang = locale
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
  }, [locale])

  const setLocale = (l: Locale) => {
    localStorage.setItem(LANG_KEY, l)
    _setLocale(l)
  }

  const t: Ctx['t'] = (k, vars) => {
    let s = DICTS[locale][k] ?? DICTS.en[k] ?? String(k)
    if (vars) {
      for (const [vk, vv] of Object.entries(vars)) {
        s = s.replaceAll(`{${vk}}`, String(vv))
      }
    }
    return s
  }

  return <LocaleCtx.Provider value={{ locale, setLocale, t }}>{children}</LocaleCtx.Provider>
}

export function useT() {
  return useContext(LocaleCtx)
}
